import { useScrollReveal } from "@/hooks/useScrollReveal";

const BG_IMAGE =
  "https://public.readdy.ai/ai/img_res/731c39f98e779f5a2d4c53aac647c01c.jpg";

const lenders = [
  { icon: "ri-bank-line", label: "50+ Lender Network" },
  { icon: "ri-star-fill", label: "All Credit Scores" },
  { icon: "ri-timer-flash-line", label: "Same-Day Approval" },
  { icon: "ri-user-heart-line", label: "First-Time Buyers" },
];

export default function FinanceBanner() {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section
      className="relative overflow-hidden py-20 md:py-24"
      aria-label="Financing solutions"
    >
      {/* Background image with deep overlay */}
      <div className="absolute inset-0">
        <img
          src={BG_IMAGE}
          alt=""
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal/96 via-charcoal/88 to-charcoal/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 via-transparent to-charcoal/40" />
      </div>

      {/* Gold accent lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-DEFAULT/50 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-DEFAULT/30 to-transparent" />

      {/* Decorative left vertical gold bar */}
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-gold-DEFAULT/40 to-transparent hidden lg:block" />

      {/* Content */}
      <div
        ref={ref}
        className={`relative z-10 max-w-7xl mx-auto px-6 md:px-10 transition-all duration-700 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left — Headline */}
          <div className="flex-1">
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-gold-DEFAULT" />
              <span className="text-gold-DEFAULT text-xs font-bold tracking-[0.22em] uppercase font-sans">
                FLEXIBLE FINANCING
              </span>
            </div>
            <h2
              className="font-serif text-white leading-tight mb-5"
              style={{ fontSize: "clamp(2rem, 3.5vw, 3rem)", lineHeight: 1.08 }}
            >
              We Work With 50+ Lenders So{" "}
              <em className="not-italic text-gold-DEFAULT">You Get Approved</em>
            </h2>
            <p className="text-white/85 font-sans text-base leading-relaxed max-w-lg mb-8">
              Good credit, bad credit, no credit history — our finance manager Muhammad
              personally works every deal to find the best possible rate and terms for
              your situation. Same-day decisions available.
            </p>

            {/* Lender trust pills */}
            <div className="flex flex-wrap gap-3 mb-9">
              {lenders.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center gap-2 bg-white/8 border border-white/10 px-4 py-2 rounded-full"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${item.icon} text-gold-DEFAULT text-sm`} />
                  </div>
                  <span className="text-white/90 text-xs font-sans font-semibold whitespace-nowrap tracking-wide">
                    {item.label}
                  </span>
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://www.uagmn.com/apply-online/"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2.5 bg-gold-DEFAULT text-charcoal font-bold text-sm px-9 py-4 rounded-full hover:bg-gold-light transition-all duration-300 whitespace-nowrap cursor-pointer tracking-wide"
              >
                Apply for Financing Now
                <div className="w-4 h-4 flex items-center justify-center transition-transform group-hover:translate-x-1 duration-300">
                  <i className="ri-arrow-right-line" />
                </div>
              </a>
              <a
                href="https://www.uagmn.com/get-pre-qualified/"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-semibold text-sm px-9 py-4 rounded-full hover:border-white/60 hover:bg-white/8 transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm"
              >
                Check Pre-Qualification
              </a>
            </div>
          </div>

          {/* Right — Stats panel */}
          <div className="flex-shrink-0 w-full lg:w-auto">
            <div className="grid grid-cols-2 gap-3 lg:gap-4 max-w-xs mx-auto lg:mx-0">
              {[
                { value: "50+", label: "Lender Relationships" },
                { value: "5.0★", label: "Google Rated" },
                { value: "Same Day", label: "Approval Decision" },
                { value: "15-Day", label: "Warranty on Every Car" },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="bg-white/6 border border-white/10 rounded-xl p-5 text-center hover:border-gold-DEFAULT/30 transition-colors"
                >
                  <div
                    className="font-serif text-gold-DEFAULT leading-none mb-2"
                    style={{ fontSize: "clamp(1.4rem, 2vw, 1.8rem)" }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-white/85 text-xs font-sans leading-tight">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Call CTA */}
            <div className="mt-5 max-w-xs mx-auto lg:mx-0">
              <a
                href="tel:9527693208"
                className="flex items-center justify-center gap-3 bg-white/5 border border-gold-DEFAULT/25 rounded-xl px-5 py-4 hover:bg-white/10 hover:border-gold-DEFAULT/50 transition-all cursor-pointer group whitespace-nowrap"
                aria-label="Call United Auto Group"
              >
                <div className="w-9 h-9 rounded-full bg-gold-DEFAULT/15 flex items-center justify-center flex-shrink-0 group-hover:bg-gold-DEFAULT/25 transition-colors">
                  <i className="ri-phone-fill text-gold-DEFAULT" />
                </div>
                <div>
                  <p className="text-white/85 text-xs font-sans">Call Us Now</p>
                  <p className="text-white font-bold text-sm font-sans">(952) 769-3208</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
